import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiAlertCircle,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiDatabase,
  FiLayers,
  FiPlay,
  FiRefreshCw,
  FiRotateCcw,
  FiSquare,
} from 'react-icons/fi';
import {
  Badge,
  Button,
  Card,
  DataTable,
  Drawer,
  EmptyState,
  IconButton,
  PageHeader,
  Skeleton,
  StatusPill,
  cn,
  toast,
} from '../components/ui';
import Modal from '../components/Modal';
import {
  baselineTenantMigrations,
  extractApiError,
  getMigrationTenants,
  getTenantMigrationStatus,
  rollbackTenantMigration,
  runTenantMigration,
} from '../servicesV2/migrationsService';

/* -------------------------------------------------------------------------- */
/*  Confirm modals                                                            */
/* -------------------------------------------------------------------------- */

function RollbackConfirmModal({ tenant, close, onConfirm, loading }) {
  const { t } = useTranslation();
  if (!tenant) return null;
  return (
    <Modal
      isOpen={!!tenant}
      close={close}
      title={t('MIGRATIONS.ROLLBACK_MODAL.TITLE', { defaultValue: 'Rollback' })}
    >
      <div className="flex items-start gap-3 rounded-md border border-danger-100 bg-danger-50 px-3 py-3">
        <FiAlertTriangle size={18} className="mt-0.5 shrink-0 text-danger-600" />
        <div>
          <p className="text-body-sm font-semibold text-danger-700">
            {t('MIGRATIONS.ROLLBACK_MODAL.CONFIRM', {
              name: tenant.Nom || tenant.Key,
              defaultValue: `Rollback du dernier batch sur ${tenant.Nom || tenant.Key} ?`,
            })}
          </p>
          <p className="mt-1 text-caption text-danger-700/90">
            {t('MIGRATIONS.ROLLBACK_MODAL.HINT', {
              defaultValue:
                'Annule la dernière série de migrations appliquée. Opération non réversible côté données.',
            })}
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
        <Button variant="secondary" disabled={loading} onClick={close}>
          {t('GLOBAL.CANCEL', { defaultValue: 'Annuler' })}
        </Button>
        <Button variant="danger" loading={loading} onClick={onConfirm}>
          {t('MIGRATIONS.ROLLBACK_MODAL.CONFIRM_BTN', { defaultValue: 'Confirmer le rollback' })}
        </Button>
      </div>
    </Modal>
  );
}

function BaselineConfirmModal({ tenant, pendingCount, close, onConfirm, loading }) {
  const { t } = useTranslation();
  if (!tenant) return null;
  return (
    <Modal
      isOpen={!!tenant}
      close={close}
      title={t('MIGRATIONS.BASELINE_MODAL.TITLE', { defaultValue: 'Baseline' })}
    >
      <div className="flex items-start gap-3 rounded-md border border-warning-100 bg-warning-50 px-3 py-3">
        <FiAlertTriangle size={18} className="mt-0.5 shrink-0 text-warning-600" />
        <div>
          <p className="text-body-sm font-semibold text-warning-700">
            {t('MIGRATIONS.BASELINE_MODAL.CONFIRM', {
              count: pendingCount ?? '?',
              key: tenant.Key,
              defaultValue: `Baseline ${pendingCount ?? '?'} migration(s) en attente pour ${tenant.Key} ?`,
            })}
          </p>
          <p className="mt-1 text-caption text-warning-700/90">
            {t('MIGRATIONS.BASELINE_MODAL.HINT', {
              defaultValue:
                'Marque les migrations en attente comme déjà appliquées sans les exécuter.',
            })}
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-center justify-end gap-2 border-t border-line pt-4">
        <Button variant="secondary" disabled={loading} onClick={close}>
          {t('GLOBAL.CANCEL', { defaultValue: 'Annuler' })}
        </Button>
        <Button variant="danger" loading={loading} onClick={onConfirm}>
          {t('MIGRATIONS.BASELINE_MODAL.CONFIRM_BTN', { defaultValue: 'Confirmer' })}
        </Button>
      </div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/*  Status drawer                                                             */
/* -------------------------------------------------------------------------- */

function MigrationList({ items, tone }) {
  const dot =
    tone === 'warning' ? 'bg-warning-500' : tone === 'success' ? 'bg-success-500' : 'bg-ink-400';
  if (!items || items.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-line bg-surface-muted p-4 text-center text-caption text-fg-subtle">
        —
      </p>
    );
  }
  return (
    <ul className="max-h-64 overflow-auto rounded-md border border-line bg-surface-muted">
      {items.map((m, i) => (
        <li
          key={`${m}-${i}`}
          className={cn(
            'flex items-center gap-2 border-b border-line/50 px-3 py-1.5 last:border-0',
            'font-mono text-caption',
            tone === 'warning' && 'text-warning-700',
            tone === 'success' && 'text-fg-muted',
          )}
        >
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', dot)} />
          <span className="truncate">{m}</span>
        </li>
      ))}
    </ul>
  );
}

function StatusDrawer({
  tenant,
  isOpen,
  close,
  data,
  loading,
  error,
  rowStatus,
  showAdvanced,
  onRun,
  onRollback,
  onBaseline,
  busy,
  running,
}) {
  const { t } = useTranslation();
  if (!tenant) return null;

  return (
    <Drawer
      isOpen={isOpen}
      close={close}
      title={tenant.Nom || tenant.Key}
      subtitle={tenant.Key}
      size="md"
    >
      <Drawer.Body className="space-y-5">
        {/* Current state */}
        <div className="flex items-center justify-between rounded-lg border border-line bg-surface-muted px-4 py-3">
          <div>
            <p className="text-micro text-fg-subtle">Statut</p>
            <div className="mt-1">
              <StatusPill status={rowStatus?.kind || 'idle'} />
            </div>
          </div>
          {data?.database && (
            <div className="text-right">
              <p className="text-micro text-fg-subtle">Database</p>
              <p className="mt-1 inline-flex items-center gap-1 font-mono text-caption text-fg">
                <FiDatabase size={12} className="text-fg-subtle" />
                {data.database}
              </p>
            </div>
          )}
        </div>

        {/* Last run result */}
        {rowStatus?.kind === 'ok' && rowStatus.result && (
          <div className="rounded-md border border-success-100 bg-success-50 p-3">
            <p className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-success-700">
              <FiCheckCircle size={14} />
              {rowStatus.result.migrations?.length
                ? t('MIGRATIONS.ROW.APPLIED', {
                    count: rowStatus.result.migrations.length,
                    batch: rowStatus.result.batchNo,
                    defaultValue: `${rowStatus.result.migrations.length} migration(s) appliquées · batch ${rowStatus.result.batchNo}`,
                  })
                : t('MIGRATIONS.ROW.UP_TO_DATE', { defaultValue: 'À jour' })}
            </p>
          </div>
        )}
        {rowStatus?.kind === 'error' && (
          <div className="rounded-md border border-danger-100 bg-danger-50 p-3">
            <p className="inline-flex items-start gap-1.5 text-body-sm text-danger-700">
              <FiAlertCircle size={14} className="mt-0.5" />
              <span>
                <span className="font-semibold">{rowStatus.code}:</span> {rowStatus.message}
              </span>
            </p>
          </div>
        )}

        {/* Loading / error of status fetch */}
        {loading && (
          <p className="text-caption text-fg-subtle">
            {t('MIGRATIONS.STATUS_MODAL.LOADING', { defaultValue: 'Chargement du statut…' })}
          </p>
        )}
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-danger-100 bg-danger-50 p-3 text-body-sm text-danger-700">
            <FiAlertCircle size={14} className="mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Pending */}
        {!loading && !error && data && (
          <>
            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-micro text-fg-subtle">
                  {t('MIGRATIONS.STATUS_MODAL.PENDING', { defaultValue: 'En attente' })}
                </h3>
                <Badge variant={data.pending?.length ? 'warning' : 'neutral'} size="sm">
                  {data.pending?.length ?? 0}
                </Badge>
              </div>
              <MigrationList items={data.pending} tone="warning" />
            </section>

            <section>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-micro text-fg-subtle">
                  {t('MIGRATIONS.STATUS_MODAL.COMPLETED', { defaultValue: 'Appliquées' })}
                </h3>
                <Badge variant="success" size="sm">
                  {data.completed?.length ?? 0}
                </Badge>
              </div>
              <MigrationList items={data.completed} tone="success" />
            </section>
          </>
        )}
      </Drawer.Body>

      <Drawer.Footer>
        {showAdvanced && (
          <>
            <Button
              variant="danger-ghost"
              size="sm"
              iconLeft={<FiRotateCcw size={13} />}
              disabled={busy || running}
              onClick={onRollback}
            >
              {t('MIGRATIONS.ROW.ROLLBACK', { defaultValue: 'Rollback' })}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconLeft={<FiLayers size={13} />}
              disabled={busy || running}
              onClick={onBaseline}
            >
              {t('MIGRATIONS.ROW.BASELINE', { defaultValue: 'Baseline' })}
            </Button>
          </>
        )}
        <Button
          variant="primary"
          iconLeft={<FiPlay size={13} />}
          loading={busy}
          disabled={running || busy}
          onClick={onRun}
        >
          {t('MIGRATIONS.ROW.RUN', { defaultValue: 'Migrer' })}
        </Button>
      </Drawer.Footer>
    </Drawer>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

function TenantMigrations() {
  const { t } = useTranslation();

  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [rowStatus, setRowStatus] = useState({});
  const [rowBusy, setRowBusy] = useState({});
  const [rowPending, setRowPending] = useState({}); // cached pending counts
  const [loadingPending, setLoadingPending] = useState(false);

  const [isLoopRunning, setIsLoopRunning] = useState(false);
  const cancelRef = useRef(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [continueOnError, setContinueOnError] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [rollbackTarget, setRollbackTarget] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  const [baselineTarget, setBaselineTarget] = useState(null);
  const [baselinePending, setBaselinePending] = useState(null);
  const [baselineLoading, setBaselineLoading] = useState(false);

  const [drawer, setDrawer] = useState({
    open: false,
    tenant: null,
    data: null,
    loading: false,
    error: null,
  });

  // Fetch pending-migration counts for every tenant in parallel, writing
  // each row's result to state as soon as it resolves (progressive display).
  const fetchPendingForAll = useCallback(async (tenantList) => {
    if (!tenantList || tenantList.length === 0) return;
    setLoadingPending(true);
    try {
      await Promise.all(
        tenantList.map(async (tenant) => {
          try {
            const data = await getTenantMigrationStatus(tenant.Key);
            setRowPending((prev) => ({
              ...prev,
              [tenant.Key]: data?.pending?.length ?? 0,
            }));
          } catch (err) {
            const { requestId } = extractApiError(err);
            if (requestId) console.error(`[pending ${tenant.Key}] requestId=${requestId}`);
            setRowPending((prev) => ({ ...prev, [tenant.Key]: null }));
          }
        }),
      );
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const fetchTenants = async () => {
    setLoadingTenants(true);
    setLoadError(null);
    try {
      const data = await getMigrationTenants();
      const list = Array.isArray(data) ? data : [];
      setTenants(list);
      // Kick off pending checks immediately — list renders while they stream in.
      fetchPendingForAll(list);
    } catch (err) {
      const { message } = extractApiError(err);
      setLoadError(message);
    } finally {
      setLoadingTenants(false);
    }
  };

  useEffect(() => {
    fetchTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = (key, status) =>
    setRowStatus((prev) => ({ ...prev, [key]: status }));
  const setBusy = (key, busy) => setRowBusy((prev) => ({ ...prev, [key]: busy }));

  const runOne = async (tenant) => {
    setBusy(tenant.Key, true);
    setStatus(tenant.Key, { kind: 'running' });
    try {
      const result = await runTenantMigration(tenant.Key);
      setStatus(tenant.Key, { kind: 'ok', result });
      // Optimistically mark pending as 0 after a successful run.
      setRowPending((p) => ({ ...p, [tenant.Key]: 0 }));
      return { ok: true, result };
    } catch (err) {
      const { message, code, requestId } = extractApiError(err);
      if (requestId) console.error(`[migrate ${tenant.Key}] requestId=${requestId}`);
      setStatus(tenant.Key, { kind: 'error', message, code });
      toast.error(`Migration échouée: ${tenant.Nom || tenant.Key}`, { description: message });
      return { ok: false };
    } finally {
      setBusy(tenant.Key, false);
    }
  };

  const runAll = async () => {
    if (isLoopRunning || tenants.length === 0) return;
    cancelRef.current = false;
    setIsLoopRunning(true);
    setProgress({ done: 0, total: tenants.length });

    let failed = 0;
    for (let i = 0; i < tenants.length; i++) {
      if (cancelRef.current) break;
      const tenant = tenants[i];
      const { ok } = await runOne(tenant);
      if (!ok) failed += 1;
      setProgress({ done: i + 1, total: tenants.length });
      if (!ok && !continueOnError) break;
    }

    setIsLoopRunning(false);
    cancelRef.current = false;
    if (failed === 0) {
      toast.success('Migrations terminées', { description: `${tenants.length} tenants` });
    } else {
      toast.warning(`Terminé avec ${failed} échec(s)`, {
        description: `${tenants.length - failed} réussis sur ${tenants.length}`,
      });
    }
  };

  const stopLoop = () => {
    cancelRef.current = true;
  };

  const confirmRollback = async () => {
    if (!rollbackTarget) return;
    const tenant = rollbackTarget;
    setRollbackLoading(true);
    setBusy(tenant.Key, true);
    setStatus(tenant.Key, { kind: 'running' });
    try {
      const result = await rollbackTenantMigration(tenant.Key);
      setStatus(tenant.Key, { kind: 'ok', result });
      toast.success('Rollback terminé', { description: tenant.Nom || tenant.Key });
    } catch (err) {
      const { message, code, requestId } = extractApiError(err);
      if (requestId) console.error(`[rollback ${tenant.Key}] requestId=${requestId}`);
      setStatus(tenant.Key, { kind: 'error', message, code });
      toast.error('Rollback échoué', { description: message });
    } finally {
      setBusy(tenant.Key, false);
      setRollbackLoading(false);
      setRollbackTarget(null);
    }
  };

  const beginBaseline = async (tenant) => {
    setBaselineTarget(tenant);
    setBaselinePending(null);
    try {
      const data = await getTenantMigrationStatus(tenant.Key);
      setBaselinePending(data?.pending?.length ?? 0);
    } catch (err) {
      const { requestId } = extractApiError(err);
      if (requestId) console.error(`[status ${tenant.Key}] requestId=${requestId}`);
      setBaselinePending(null);
    }
  };

  const confirmBaseline = async () => {
    if (!baselineTarget) return;
    const tenant = baselineTarget;
    setBaselineLoading(true);
    setBusy(tenant.Key, true);
    setStatus(tenant.Key, { kind: 'running' });
    try {
      const data = await baselineTenantMigrations(tenant.Key);
      setStatus(tenant.Key, {
        kind: 'ok',
        result: {
          tenantKey: data.tenantKey,
          database: data.database,
          batchNo: data.batchNo,
          migrations: data.baselined || [],
        },
      });
      setRowPending((p) => ({ ...p, [tenant.Key]: 0 }));
      toast.success('Baseline terminée', { description: tenant.Nom || tenant.Key });
    } catch (err) {
      const { message, code, requestId } = extractApiError(err);
      if (requestId) console.error(`[baseline ${tenant.Key}] requestId=${requestId}`);
      setStatus(tenant.Key, { kind: 'error', message, code });
      toast.error('Baseline échouée', { description: message });
    } finally {
      setBusy(tenant.Key, false);
      setBaselineLoading(false);
      setBaselineTarget(null);
      setBaselinePending(null);
    }
  };

  const openDrawer = async (tenant) => {
    setDrawer({ open: true, tenant, data: null, loading: true, error: null });
    try {
      const data = await getTenantMigrationStatus(tenant.Key);
      setDrawer({ open: true, tenant, data, loading: false, error: null });
      setRowPending((p) => ({ ...p, [tenant.Key]: data?.pending?.length ?? 0 }));
    } catch (err) {
      const { message, requestId } = extractApiError(err);
      if (requestId) console.error(`[status ${tenant.Key}] requestId=${requestId}`);
      setDrawer({ open: true, tenant, data: null, loading: false, error: message });
    }
  };
  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));

  const checkAllPending = async () => {
    if (loadingPending || isLoopRunning) return;
    await fetchPendingForAll(tenants);
    toast.success('Vérification terminée');
  };

  const progressPct = useMemo(() => {
    if (!progress.total) return 0;
    return Math.round((progress.done / progress.total) * 100);
  }, [progress]);

  const anyRowBusy = Object.values(rowBusy).some(Boolean);

  const pendingTotal = useMemo(
    () => Object.values(rowPending).reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0),
    [rowPending],
  );

  const columns = [
    {
      id: 'status',
      header: '',
      width: 40,
      stopRowClick: false,
      cell: (row) => {
        const status = rowStatus[row.Key];
        return <StatusPill status={status?.kind || 'idle'} size="sm" />;
      },
    },
    {
      id: 'tenant',
      header: 'Tenant',
      accessor: (row) => row.Nom || row.Key,
      sortable: true,
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate text-body-sm font-medium text-fg">
            {row.Nom || row.Key}
          </div>
          <div className="truncate font-mono text-caption text-fg-subtle">{row.Key}</div>
        </div>
      ),
    },
    {
      id: 'pending',
      header: 'En attente',
      accessor: (row) => rowPending[row.Key] ?? -1,
      sortable: true,
      cell: (row) => {
        const p = rowPending[row.Key];
        if (p == null) {
          // While the initial (or manual) pending check is still in flight,
          // show a shimmer so rows don't look like "no data".
          if (loadingPending) return <Skeleton className="h-4 w-20" />;
          return <span className="text-caption text-fg-subtle">—</span>;
        }
        if (p === 0) {
          return (
            <Badge variant="success" size="sm">
              À jour
            </Badge>
          );
        }
        return (
          <Badge variant="warning" size="sm">
            {p} en attente
          </Badge>
        );
      },
    },
    {
      id: 'info',
      header: 'Dernier run',
      cell: (row) => {
        const s = rowStatus[row.Key];
        if (!s) return <span className="text-caption text-fg-subtle">—</span>;
        if (s.kind === 'running') {
          return <span className="text-caption text-info-700">En cours…</span>;
        }
        if (s.kind === 'ok') {
          const applied = s.result?.migrations?.length ?? 0;
          return (
            <span className="text-caption text-success-700">
              {applied > 0
                ? `${applied} appliquée${applied > 1 ? 's' : ''} · batch ${s.result.batchNo}`
                : 'Aucun changement'}
            </span>
          );
        }
        if (s.kind === 'error') {
          return (
            <span className="inline-flex items-center gap-1 truncate text-caption text-danger-700" title={s.message}>
              <FiAlertCircle size={11} />
              {s.code}
            </span>
          );
        }
        return <span className="text-caption text-fg-subtle">—</span>;
      },
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      width: showAdvanced ? 220 : 120,
      stopRowClick: true,
      cell: (row) => {
        const busy = !!rowBusy[row.Key];
        return (
          <div className="flex items-center justify-end gap-1">
            <Button
              size="sm"
              iconLeft={<FiPlay size={12} />}
              disabled={busy || isLoopRunning}
              onClick={() => runOne(row)}
            >
              {t('MIGRATIONS.ROW.RUN', { defaultValue: 'Migrer' })}
            </Button>
            {showAdvanced && (
              <>
                <IconButton
                  variant="danger"
                  size="sm"
                  tooltip={t('MIGRATIONS.ROW.ROLLBACK', { defaultValue: 'Rollback' })}
                  disabled={busy || isLoopRunning}
                  onClick={() => setRollbackTarget(row)}
                >
                  <FiRotateCcw />
                </IconButton>
                <IconButton
                  variant="subtle"
                  size="sm"
                  tooltip={t('MIGRATIONS.ROW.BASELINE', { defaultValue: 'Baseline' })}
                  disabled={busy || isLoopRunning}
                  onClick={() => beginBaseline(row)}
                >
                  <FiLayers />
                </IconButton>
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader
        title={t('MIGRATIONS.TITLE', { defaultValue: 'Tenant migrations' })}
        count={tenants.length}
        description="État et exécution des migrations par tenant."
        meta={
          pendingTotal > 0 && (
            <Badge variant="warning" dot>
              {pendingTotal} migration{pendingTotal > 1 ? 's' : ''} en attente (total)
            </Badge>
          )
        }
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="md"
              iconLeft={<FiRefreshCw size={13} className={loadingPending ? 'animate-spin' : ''} />}
              disabled={loadingPending || loadingTenants || isLoopRunning}
              onClick={checkAllPending}
            >
              Vérifier tout
            </Button>
            {isLoopRunning ? (
              <Button variant="danger" iconLeft={<FiSquare size={13} />} onClick={stopLoop}>
                {t('MIGRATIONS.STOP', { defaultValue: 'Arrêter' })}
              </Button>
            ) : (
              <Button
                variant="primary"
                iconLeft={<FiPlay size={13} />}
                disabled={loadingTenants || tenants.length === 0 || anyRowBusy}
                onClick={runAll}
              >
                {t('MIGRATIONS.MIGRATE_ALL', { defaultValue: 'Migrer tout' })}
              </Button>
            )}
          </div>
        }
      />

      {/* Options bar */}
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 shadow-e1">
        <label className="inline-flex cursor-pointer items-center gap-2 text-body-sm text-fg-muted">
          <input
            type="checkbox"
            checked={continueOnError}
            onChange={(e) => setContinueOnError(e.target.checked)}
            disabled={isLoopRunning}
            className="h-4 w-4 rounded border-line text-brand-600 focus:ring-brand-500"
          />
          <span>
            {t('MIGRATIONS.CONTINUE_ON_ERROR', {
              defaultValue: 'Continuer malgré les erreurs',
            })}
          </span>
        </label>

        <div className="h-4 w-px bg-line" />

        <label className="inline-flex cursor-pointer items-center gap-2 text-body-sm text-fg-muted">
          <input
            type="checkbox"
            checked={showAdvanced}
            onChange={(e) => setShowAdvanced(e.target.checked)}
            className="h-4 w-4 rounded border-line text-brand-600 focus:ring-brand-500"
          />
          <span>Actions avancées (rollback, baseline)</span>
        </label>
      </div>

      {/* Run progress */}
      {(isLoopRunning || progress.total > 0) && (
        <Card padding="md" className="mb-4">
          <div className="mb-2 flex items-center justify-between text-body-sm">
            <span className="inline-flex items-center gap-1.5 font-semibold text-fg">
              <FiClock size={14} className="text-info-600" />
              {t('MIGRATIONS.PROGRESS', {
                done: progress.done,
                total: progress.total,
                defaultValue: `${progress.done} / ${progress.total}`,
              })}
            </span>
            <span className="font-mono tabular-nums text-fg-muted">{progressPct}%</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-ink-100">
            <div
              className={cn(
                'h-full rounded-full transition-all duration-300',
                isLoopRunning ? 'bg-info-500' : 'bg-success-500',
              )}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </Card>
      )}

      {loadError ? (
        <EmptyState
          variant="danger"
          icon={<FiAlertCircle size={22} />}
          title="Chargement impossible"
          description={loadError}
          action={<Button onClick={fetchTenants}>Réessayer</Button>}
        />
      ) : (
        <DataTable
          columns={columns}
          data={tenants}
          rowKey="id"
          onRowClick={openDrawer}
          loading={loadingTenants}
          empty={
            <EmptyState
              icon={<FiDatabase size={22} />}
              title={t('MIGRATIONS.EMPTY', { defaultValue: 'Aucun tenant à migrer' })}
              description="Ajoutez des clients pour les voir apparaître ici."
            />
          }
          initialSort={{ columnId: 'tenant', direction: 'asc' }}
          pageSize={40}
        />
      )}

      {/* Drawer */}
      <StatusDrawer
        tenant={drawer.tenant}
        isOpen={drawer.open}
        close={closeDrawer}
        data={drawer.data}
        loading={drawer.loading}
        error={drawer.error}
        rowStatus={drawer.tenant ? rowStatus[drawer.tenant.Key] : null}
        showAdvanced={showAdvanced}
        busy={drawer.tenant ? !!rowBusy[drawer.tenant.Key] : false}
        running={isLoopRunning}
        onRun={() => drawer.tenant && runOne(drawer.tenant)}
        onRollback={() => {
          if (drawer.tenant) setRollbackTarget(drawer.tenant);
        }}
        onBaseline={() => {
          if (drawer.tenant) beginBaseline(drawer.tenant);
        }}
      />

      {/* Confirmation modals */}
      <RollbackConfirmModal
        tenant={rollbackTarget}
        close={() => !rollbackLoading && setRollbackTarget(null)}
        onConfirm={confirmRollback}
        loading={rollbackLoading}
      />
      <BaselineConfirmModal
        tenant={baselineTarget}
        pendingCount={baselinePending}
        close={() => {
          if (!baselineLoading) {
            setBaselineTarget(null);
            setBaselinePending(null);
          }
        }}
        onConfirm={confirmBaseline}
        loading={baselineLoading}
      />
    </>
  );
}

export default TenantMigrations;
